import type { IAddon, IData, IDataPoint, IEvent, IItem, TDescriptionTable } from 'Components/sq-graphics/interfaces';
import {
  exists,
  formatTime,
  isJSObjectType,
  isNumber,
  isPartialDate,
  partialDateFromDate,
  partialDateToValue,
  sortPartialDate,
} from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { DateTime } from 'luxon';
import { defaultPapSettingsUnits, defaultPapTreatmentResponseUnits } from 'Routes/Treatment/Document/config';
import { papSettingsFields, papTreatmentResponseFields } from 'Routes/Treatment/Document/definitions';
import { patientHasRespiratoryFailureDiagnosis, patientHasSleepApneaDiagnosis } from 'Routes/Treatment/utils';
import type { FDataFieldLocalizer } from './createGraphDescriptions';
import createGraphDescriptions from './createGraphDescriptions';
import type { IReadonlyDocument, IReadonlyDocumentWithType } from 'Store/readonly/readonlyGlobals';
import type { IOxygenTherapySettings } from 'neuro-schemas/oxygenTherapy';
import { filterDocumentsByType } from 'Utility/documentHandling';

type TTreatmentType =
  | IPatientDoesNotWantRespiratorySupportTherapy
  | IOxygenTherapy
  | IOxygenTherapySettings
  | IHFNCTherapy
  | IHFNCSetting
  | IPAPTherapy
  | IMADTherapy
  | IMADControl
  | ISleepApneaSurgicalTreatment
  | ISurgicalTreatmentControl
  | IOtherTreatment;

const getTreatmentUnit = (fieldName: string, fm: (id: string) => string): { [key: string]: string } | string => {
  return (
    {
      ...defaultPapSettingsUnits,
      ...defaultPapTreatmentResponseUnits,
      backgroundFrequency: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      daysWithLessThan4HoursOfUse: fm('treatment.papTherapy.day'),
      respiratoryRate95Percentile: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      respiratoryRateMedian: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      respiratoryRateMin: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      respiratoryRateMax: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      flow: fm('treatment.oxygenTherapy.settings.litersPerMinute'),
      flowRate: fm('treatment.hfncTherapy.settings.litersPerMin'),
      supplementalOxygen: '%',
      oxygenFlow: 'l/min',
      oxygenFlowPercent: '%',
      oxygenSaturation: '%',
      respiratoryRate: fm('treatment.papTherapy.inhalationsPerMinuteShort'),
      '': fm('treatment.madTherapy.controls.madUsageFrequencyPlaceholder'),
    }[fieldName] ?? ''
  );
};

const createTreatmentTooltipDescription = (
  d: TTreatmentType | IReadonlyDocument,
  fields: string[],
  locPath: string,
  optsLocPath: string,
  fm: (id: string) => string,
  freeTextFields?: string[],
): IItem['description'] | IEvent['description'] => {
  return fields.map(f => {
    const value = d[f as keyof TTreatmentType] as unknown as number | string | boolean | Time;

    const isFreeTextField = (field: string) => (freeTextFields ?? []).includes(field);

    const getUnit = () => {
      switch (f) {
        case 'flow': {
          if ('implementationOfTreatment' in d && d.implementationOfTreatment === 'venturiMask') {
            return getTreatmentUnit('oxygenFlowPercent', fm);
          }
          return getTreatmentUnit(f, fm);
        }
        default: {
          return getTreatmentUnit(f, fm);
        }
      }
    };

    const formatValue = () => {
      switch (typeof value) {
        case 'number': {
          return `${value} ${getUnit()}`;
        }
        case 'string': {
          return isFreeTextField(f) ? value : fm(optsLocPath + value);
        }
        case 'boolean': {
          return value ? fm('general.yes') : fm('general.no');
        }
        default: {
          if (Array.isArray(value)) {
            if (f === 'time') {
              return formatTime(value);
            }

            if (f === 'flow' && value.every(v => typeof v === 'number')) {
              return `${value[0]}\u00A0–\u00A0${value[1]} ${getUnit()}`;
            }

            return value
              .map((v: any) => {
                let localizedValue = fm(optsLocPath + v);
                if (v === 'other' && 'endReasonOther' in d) {
                  localizedValue += `${d.endReasonOther ?? '-'}`;
                }
                return localizedValue;
              })
              .join(', ');
          }
          break;
        }
      }
      return undefined;
    };

    return {
      title: fm(locPath + f),
      values: formatValue(),
    };
  });
};

// Used with pap treatment's treatment responses
const deviceMapper = (device: string): string =>
  device === 'autosetForHer'
    ? 'selfAdjustingCPAP'
    : ['selfAdjusting2PV', 'adaptiveServoVentilator', 'automaticAdaptiveServoVentilator'].includes(device)
      ? 'constantPressure2PV'
      : device;

export const convertTreatmentToTimeline = (
  docs: Array<
    IDocumentWithTypeV2<
      | 'patientDoesNotWantRespiratorySupportTherapy'
      | 'oxygenTherapy'
      | 'hfncTherapy'
      | 'papTherapy'
      | 'madTherapy'
      | 'sleepApneaSurgicalTreatment'
      | 'otherTreatment'
    >
  >,
  resMedDevices: Array<IReadonlyDocument>,
  resMedSettings: Array<IReadonlyDocument>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IAddon> | undefined => {
  if (docs.length === 0 && resMedDevices.length === 0 && resMedSettings.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(d => {
    switch (d._type) {
      case 'patientDoesNotWantRespiratorySupportTherapy': {
        if (!isPartialDate(d.date)) return;
        const items: IItem[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('treatment.patientDoesNotWantRespiratorySupportTherapy.title'),
        });
        addons.push({
          id: d._type,
          title: fm('treatment.patientDoesNotWantRespiratorySupportTherapy.title'),
          items,
          events: [],
        });
        break;
      }
      case 'oxygenTherapy': {
        if (!d.oxygenTherapyType) return;
        if (!isPartialDate(d.date)) return;
        const items: IItem[] = [];
        const events: IEvent[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: d.endDate ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm(
            `treatment.oxygenTherapy.opts.${d.oxygenTherapyType}${
              d.oxygenTherapyType === 'stressOxygen' ? 'Short' : ''
            }`,
          ),
          description: createTreatmentTooltipDescription(
            d,
            ['treatmentStartLocation', 'treatmentStart'],
            'treatment.oxygenTherapy.',
            'treatment.oxygenTherapy.opts.',
            fm,
          ),
        });
        (d.settings || []).forEach((s: IOxygenTherapySettings) => {
          const sCopy: Omit<IOxygenTherapySettings, 'flow'> & { flow?: number | [number, number] } = structuredClone(s);
          if ('flowMin' in s || 'flowMax' in s) {
            sCopy.flow =
              exists(s.flowMin) && exists(s.flowMax)
                ? [s.flowMin as number, s.flowMax as number]
                : (s.flowMin ?? s.flowMax);
          }
          // Description pitää duunata viel
          s.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(s.date),
              eventType: 'treatment',
              title: fm('treatment.oxygenTherapy.settings.setting'),
              description: createTreatmentTooltipDescription(
                sCopy,
                ['flow', 'additionalInformation', 'implementationOfTreatment', 'humidifierInUse'],
                'treatment.oxygenTherapy.settings.',
                'treatment.oxygenTherapy.opts.',
                fm,
                ['additionalInformation'],
              ),
            });
        });
        (d.treatmentResponses || []).forEach(s => {
          // Description pitää duunata viel
          s.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(s.date),
              eventType: 'treatment',
              title: fm('treatment.oxygenTherapy.treatmentResponses.treatmentResponse'),
              description: createTreatmentTooltipDescription(
                s,
                ['oxygenSaturation'],
                'treatment.oxygenTherapy.treatmentResponses.',
                'treatment.oxygenTherapy.opts.',
                fm,
              ),
            });
        });
        isPartialDate(d.endDate) &&
          d.hasEnded?.[0] === true &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.endDate),
            eventType: 'treatment',
            title: fm('treatment.treatmentEndedTitle'),
            description: createTreatmentTooltipDescription(
              d,
              ['endReason'],
              'treatment.',
              'treatment.opts.oxygenTherapyEndReason.',
              fm,
            ),
          });
        addons.push({
          id: d.oxygenTherapyType,
          title: fm(
            `treatment.oxygenTherapy.opts.${d.oxygenTherapyType}${
              d.oxygenTherapyType === 'stressOxygen' ? 'Short' : ''
            }`,
          ),
          titleDescription: undefined,
          items,
          events,
        });
        break;
      }
      case 'hfncTherapy': {
        if (!isPartialDate(d.date)) return;
        const items: IItem[] = [];
        const events: IEvent[] = [];
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: d.endDate ? dateFromPartialUpdateTimeframe(d.endDate, [23, 59]) : undefined,
          title: fm('treatment.hfncTherapy.title'),
          description: createTreatmentTooltipDescription(
            d,
            ['treatmentStartLocation', 'treatmentStart'],
            'treatment.hfncTherapy.',
            'treatment.hfncTherapy.opts.',
            fm,
          ),
        });
        (d.settings || []).forEach(s => {
          s.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(s.date, s.time),
              eventType: 'treatment',
              title: fm('treatment.hfncTherapy.settings.setting'),
              description: createTreatmentTooltipDescription(
                s,
                ['time', 'HFNCTreatmentType', 'flowRate', 'supplementalOxygen', 'oxygenFlow'],
                'treatment.hfncTherapy.settings.',
                'treatment.hfncTherapy.settings.',
                fm,
              ),
            });
        });
        isPartialDate(d.endDate) &&
          d.hasEnded?.[0] === true &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.endDate, [23, 59]),
            eventType: 'treatment',
            title: fm('treatment.treatmentEndedTitle'),
            description: createTreatmentTooltipDescription(
              d,
              ['endReason'],
              'treatment.',
              'treatment.opts.hfncTherapyEndReason.',
              fm,
            ),
          });
        addons.push({
          id: d._type,
          title: fm('treatment.hfncTherapy.title'),
          titleDescription: undefined,
          items,
          events,
        });
        break;
      }
      case 'papTherapy': {
        if (!isPartialDate(d.date)) return;
        const items: IItem[] = [];
        const events: IEvent[] = [];
        // Push the bar into items array
        items.push({
          start: dateFromPartialUpdateTimeframe(d.date),
          end: d.endDate ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
          title: fm('treatment.papTherapy.title'),
          description: createTreatmentTooltipDescription(
            d,
            ['treatmentStartLocation', 'selfInitiated'],
            'treatment.papTherapy.',
            'treatment.papTherapy.opts.',
            fm,
          ),
        });
        // Push treatment decision date into events array
        isPartialDate(d.treatmentDecisionDate) &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.treatmentDecisionDate),
            eventType: 'treatment',
            title: fm('treatment.papTherapy.treatmentDecisionDate'),
            description: createTreatmentTooltipDescription(
              d,
              [],
              'treatment.papTherapy.',
              'treatment.papTherapy.opts.',
              fm,
            ),
          });
        // Push devices into events array
        (d.devices ?? []).forEach(d => {
          d.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'treatment',
              title: fm('treatment.papTherapy.device'),
              description: createTreatmentTooltipDescription(
                d,
                ['type', 'manufacturer', 'deviceModel'],
                'treatment.papTherapy.',
                'treatment.papTherapy.opts.',
                fm,
                ['deviceModel'],
              ),
            });
        });
        // Push settings into events array
        (d.settings ?? []).forEach(s => {
          const targetDevice = Array.isArray(d.devices) && d.devices.find(d => d.id === s.deviceId);
          const deviceType = targetDevice ? targetDevice.type : undefined;

          const fields = (deviceType ? (papSettingsFields[deviceType] ?? []) : []).filter(
            f => !f.condition || f.condition(s),
          );

          s.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(s.date),
              eventType: 'treatment',
              title: fm('treatment.papTherapy.setting'),
              description: createTreatmentTooltipDescription(
                s,
                fields.map(f => f.name),
                'treatment.papTherapy.',
                'treatment.papTherapy.opts.',
                fm,
                ['trigger', 'cycle', 'mode'],
              ),
            });
        });
        // Push implementation methods into events array
        (d.implementationMethods ?? []).forEach(m => {
          const fields = [
            'implementation',
            'maskBrand',
            'maskSize',
            'additionalInformation',
            'tubeType',
            'tubeName',
            'humidifierInUse',
            'filtersGiven',
          ];
          const filteredFields = m.tubeType !== 'heatTube' ? fields.filter(f => f !== 'tubeName') : fields;
          m.date &&
            events.push({
              date: dateFromPartialUpdateTimeframe(m.date),
              eventType: 'treatment',
              title: fm('treatment.papTherapy.implementationMethod'),
              description: createTreatmentTooltipDescription(
                m,
                filteredFields,
                'treatment.papTherapy.',
                'treatment.papTherapy.opts.',
                fm,
                ['maskBrand', 'maskSize', 'additionalInformation', 'tubeName', 'filtersGiven'],
              ),
            });
        });

        // Push treatment responses into events array
        if (Array.isArray(d.treatmentResponses)) {
          d.treatmentResponses.forEach(t => {
            // Assign device type related fields into a variable called fields
            const fields = papTreatmentResponseFields[
              deviceMapper((d.devices || []).find(d => d.id === t.deviceId)?.type ?? '')
            ]
              ?.filter(f => !f.condition || f.condition(t))
              .map(i => i.name)
              .filter(f => f !== 'startDateOfPeriodUnderReview')
              .filter(f => isJSObjectType(t) && (t[f as keyof typeof t] || t[f as keyof typeof t] === 0));

            t.date &&
              events.push({
                date: dateFromPartialUpdateTimeframe(t.date),
                eventType: 'treatment',
                title: fm('treatment.papTherapy.treatmentResponse'),
                description: createTreatmentTooltipDescription(
                  t,
                  fields ?? [],
                  'treatment.papTherapy.',
                  'treatment.papTherapy.opts.',
                  fm,
                ),
              });
          });
        }
        // Push treatment ending into events array
        isPartialDate(d.endDate) &&
          d.hasEnded?.[0] === true &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.endDate),
            eventType: 'treatment',
            title: fm('treatment.treatmentEndedTitle'),
            description: createGraphDescriptions<IPAPTherapy>(d, [
              {
                dataKey: 'endReason',
                localizers: {
                  title: () => fm('treatment.endReason'),
                  values: getPapTherapyEndReasonLocalizer(fm),
                },
              },
              {
                dataKey: 'endReasonOther',
                localizers: {
                  title: () => fm('treatment.endReasonOther'),
                  values: (d.endReason ?? []).includes('other') ? d => d.endReasonOther ?? '-' : undefined,
                },
              },
            ]),
          });
        // Push the addon into addons array
        addons.push({
          id: d._type,
          title: fm('treatment.papTherapy.title'),
          titleDescription: undefined,
          items,
          events,
        });
        break;
      }
      case 'madTherapy': {
        // If no decision is made about starting treatment, do not do anything
        if (!isPartialDate(d.treatmentDecisionDate) || !('isTreatmentStarted' in d)) return;
        const items: IItem[] = [];
        const events: IEvent[] = [];
        if (d.isTreatmentStarted === false) {
          events.push({
            date: dateFromPartialUpdateTimeframe(d.treatmentDecisionDate),
            eventType: 'treatment',
            title: fm('treatment.madTherapy.treatmentDecisionDate'),
            description: createTreatmentTooltipDescription(
              d,
              ['isTreatmentStarted', 'reasonForNotStartingTreatment'],
              'treatment.madTherapy.',
              'treatment.madTherapy.opts.',
              fm,
            ),
          });
        } else {
          d.date &&
            items.push({
              start: dateFromPartialUpdateTimeframe(d.date),
              end: d.endDate ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
              title: fm('treatment.madTherapy.title'),
              description: createTreatmentTooltipDescription(
                d,
                ['treatmentStartLocation'],
                'treatment.madTherapy.',
                'treatment.madTherapy.opts.',
                fm,
              ),
            });
          // Push treatment decision into events array
          isPartialDate(d.treatmentDecisionDate) &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.treatmentDecisionDate),
              eventType: 'treatment',
              title: fm('treatment.madTherapy.treatmentDecisionDate'),
              description: createTreatmentTooltipDescription(
                d,
                ['isTreatmentStarted'],
                'treatment.madTherapy.',
                'treatment.madTherapy.opts.',
                fm,
              ),
            });
          // Push control visits into events array
          (d.controls ?? []).forEach(c => {
            c.date &&
              events.push({
                date: dateFromPartialUpdateTimeframe(c.date),
                eventType: 'treatment',
                title: fm('treatment.madTherapy.controls.control'),
                description: createTreatmentTooltipDescription(
                  c,
                  [
                    'controlType',
                    'madUsageFrequency',
                    'fatiqueDuringTheDay',
                    'adverseEffects',
                    'details',
                    'deviceAdjustment',
                  ],
                  'treatment.madTherapy.controls.',
                  'treatment.madTherapy.controls.opts.',
                  fm,
                  ['details'],
                ),
              });
          });
          // Push treatment ending into events array
          isPartialDate(d.endDate) &&
            d.hasEnded?.[0] === true &&
            events.push({
              date: dateFromPartialUpdateTimeframe(d.endDate),
              eventType: 'treatment',
              title: fm('treatment.treatmentEndedTitle'),
              description: createTreatmentTooltipDescription(
                d,
                ['endReason'],
                'treatment.',
                'treatment.opts.madTherapyEndReason.',
                fm,
              ),
            });
        }
        addons.push({
          id: d._type,
          title: fm('treatment.madTherapy.title'),
          titleDescription: undefined,
          items,
          events,
        });
        break;
      }
      case 'sleepApneaSurgicalTreatment': {
        if (!isPartialDate(d.date) && !isPartialDate(d.treatmentDecisionDate)) return;
        const events: IEvent[] = [];
        isPartialDate(d.treatmentDecisionDate) &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.treatmentDecisionDate),
            eventType: 'treatment',
            title: fm('treatment.surgicalTreatment.treatmentDecision'),
          });
        const descriptionFields: string[] =
          d.operationType === 'bariatricSurgery'
            ? ['operationType', 'bariatricSurgeryType', 'primaryOrReoperation', 'reoperationNeed']
            : d.operationType === 'knkSurgery'
              ? ['operationType', 'knkType']
              : ['operationType'];

        const diseDescriptionFields: Array<
          keyof Pick<ISleepApneaSurgicalTreatment, 'nose' | 'velum' | 'lateralWalls' | 'tongueBase' | 'epiglottis'>
        > = ['nose', 'velum', 'lateralWalls', 'tongueBase', 'epiglottis'];
        const diseDesc: TDescriptionTable = [
          {
            title: '',
            values: <span style={{ fontWeight: 600 }}>{fm('treatment.surgicalTreatment.dise.obstructionDegree')}</span>,
          },
        ];
        diseDescriptionFields.forEach(f => {
          const obstructionValue = d[f]?.obstructionDegree;
          diseDesc.push({
            title: fm(`treatment.surgicalTreatment.dise.${f}`),
            values:
              obstructionValue || obstructionValue === 0
                ? fm(`treatment.surgicalTreatment.opts.obstructionDegree.${obstructionValue}.${f}`)
                : '-',
          });
        });

        isPartialDate(d.date) &&
          events.push({
            date: dateFromPartialUpdateTimeframe(d.date),
            eventType: 'treatment',
            title: fm('treatment.surgicalTreatment.operation'),
            description:
              d.operationType === 'dise'
                ? diseDesc
                : createTreatmentTooltipDescription(
                    d,
                    descriptionFields,
                    'treatment.surgicalTreatment.',
                    'treatment.surgicalTreatment.opts.',
                    fm,
                  ),
          });
        // control visits (knk surgery)
        (d.controlVisits ?? []).forEach(cv => {
          if (!isPartialDate(cv.date)) return;
          const descFields = [
            'controlType',
            'patientPleasedWithSurgery',
            'pleasedInformation',
            'dayTimeTiredness',
            'monitoringPolygraphyDone',
            'treatmentComplication',
          ];
          if (cv.treatmentComplication === 'yes') descFields.push('complicationInformation');
          const desc = createTreatmentTooltipDescription(
            cv,
            descFields,
            'treatment.surgicalTreatment.controls.',
            'treatment.surgicalTreatment.controls.opts.',
            fm,
            ['pleasedInformation', 'complicationInformation'],
          );
          events.push({
            date: dateFromPartialUpdateTimeframe(cv.date),
            eventType: 'controlVisit',
            title: fm('treatment.surgicalTreatment.controls.control'),
            description: desc,
          });
        });
        // jaw surgery control visits
        (d.controlVisitsJawSurgery ?? []).forEach(cv => {
          if (!isPartialDate(cv.date)) return;
          const descFields = ['controlType', 'dayTimeTiredness'];
          const desc = createTreatmentTooltipDescription(
            cv,
            descFields,
            'treatment.surgicalTreatment.controls.',
            'treatment.surgicalTreatment.controls.opts.',
            fm,
            ['pleasedInformation', 'complicationInformation'],
          );
          events.push({
            date: dateFromPartialUpdateTimeframe(cv.date),
            eventType: 'controlVisit',
            title: fm('treatment.surgicalTreatment.controls.control'),
            description: desc,
          });
        });
        addons.push({
          id: d.operationType ?? d._type,
          title:
            d.operationType === 'dise'
              ? 'DISE'
              : d.operationType
                ? fm(`treatment.surgicalTreatment.opts.${d.operationType}`)
                : fm('treatment.surgicalTreatment.title'),
          items: undefined,
          titleDescription: d.operationType === 'dise' ? fm('treatment.surgicalTreatment.opts.dise') : undefined,
          events,
        });
        break;
      }
      case 'otherTreatment': {
        if (!isPartialDate(d.date)) return;
        const items: IItem[] = [];
        switch (d.otherTreatmentType) {
          case 'postureTreatment': {
            items.push({
              start: dateFromPartialUpdateTimeframe(d.date),
              end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
              title: fm('treatment.otherTreatment.opts.postureTreatment'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d.otherTreatmentType,
              title: fm('treatment.otherTreatment.opts.postureTreatment'),
              items,
              events: [],
            });
            break;
          }
          case 'lifestyleTreatment': {
            items.push({
              start: dateFromPartialUpdateTimeframe(d.date),
              end: undefined,
              title: fm('treatment.otherTreatment.opts.lifestyleTreatment'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d.otherTreatmentType,
              title: fm('treatment.otherTreatment.opts.lifestyleTreatment'),
              items,
              events: [],
            });
            break;
          }
          case 'referralToNutritionist': {
            const events: IEvent[] = [];
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'treatment',
              title: fm('treatment.otherTreatment.opts.referralToNutritionist'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d.otherTreatmentType,
              title: fm('treatment.otherTreatment.opts.referralToNutritionist'),
              items: undefined,
              events,
            });
            break;
          }
          case 'referralToWeightControl': {
            const events: IEvent[] = [];
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'treatment',
              title: fm('treatment.otherTreatment.opts.referralToWeightControl'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d.otherTreatmentType,
              title: fm('treatment.otherTreatment.opts.referralToWeightControl'),
              items: undefined,
              events,
            });
            break;
          }
          case 'psychiatristConsultation': {
            const events: IEvent[] = [];
            events.push({
              date: dateFromPartialUpdateTimeframe(d.date),
              eventType: 'treatment',
              title: fm('treatment.otherTreatment.opts.psychiatristConsultation'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d.otherTreatmentType,
              title: fm('treatment.otherTreatment.opts.psychiatristConsultation'),
              items: undefined,
              events,
            });
            break;
          }
          default: {
            items.push({
              start: dateFromPartialUpdateTimeframe(d.date),
              end: isPartialDate(d.endDate) ? dateFromPartialUpdateTimeframe(d.endDate) : undefined,
              title: fm('treatment.otherTreatment.title'),
              description: d.otherTreatmentDetails ?? undefined,
            });
            addons.push({
              id: d._type,
              title: fm('treatment.otherTreatment.title'),
              items,
              events: [],
            });
            break;
          }
        }
      }
    }
  });
  resMedDevices.forEach(rd => {
    if (!isPartialDate(rd.date)) return;
    const events: IEvent[] = [];
    events.push({
      date: dateFromPartialUpdateTimeframe(rd.date),
      eventType: 'treatment',
      title: fm('treatment.papTherapy.device'),
      description: createTreatmentTooltipDescription(
        rd,
        ['manufacturer', 'deviceModel', 'mode'],
        'treatment.papTherapy.',
        'treatment.papTherapy.opts.',
        fm,
        ['manufacturer', 'deviceModel', 'mode'],
      ),
    });

    addons.push({
      id: 'papTherapy',
      title: fm('treatment.papTherapy.title'),
      titleDescription: undefined,
      events,
    });
  });
  resMedSettings.forEach(rs => {
    if (!isPartialDate(rs.date)) return;
    const events: IEvent[] = [];

    const settingsFields = Object.keys(rs).filter(k => !['_id', '_type', '_source', 'id', 'date'].includes(k));

    events.push({
      date: dateFromPartialUpdateTimeframe(rs.date),
      eventType: 'treatment',
      title: fm('treatment.papTherapy.setting'),
      description: createTreatmentTooltipDescription(
        rs,
        settingsFields,
        'treatment.papTherapy.',
        'treatment.papTherapy.opts.',
        fm,
        ['trigger', 'cycle', 'mode'],
      ),
    });

    addons.push({
      id: 'papTherapy',
      title: fm('treatment.papTherapy.title'),
      titleDescription: undefined,
      events,
    });
  });
  return addons.length > 0 ? addons : undefined;
};

const getClpAndCnpTestsUnit = (fieldName: string, fm: (id: string) => string): string | undefined => {
  const units: { [key: string]: string } = {
    tcCO2max: 'clpAndCnpTests.kPa',
    tcCO2mean: 'clpAndCnpTests.kPa',
    tcCO2min: 'clpAndCnpTests.kPa',
    saO295thPercentile: '%',
    saO2Median: '%',
    saO2Minimum: '%',
    saO2GreaterThan88: '%',
    saO2LessThan88: 'clpAndCnpTests.minutes',
    odi4: 'clpAndCnpTests.perHour',
    pulse: 'clpAndCnpTests.bpm',
    ahi: 'clpAndCnpTests.perHour',
    ahiSupine: 'clpAndCnpTests.perHour',
    rei: 'clpAndCnpTests.perHour',
    reiSupine: 'clpAndCnpTests.perHour',
    odi3: 'clpAndCnpTests.perHour',
    cai: 'clpAndCnpTests.perHour',
    averageSpO2: '%',
    spO2Below90: 'clpAndCnpTests.percentsOfNight',
    lowestSpO2: '%',
    respiratoryRate: 'clpAndCnpTests.perMinute',
    plmi: 'clpAndCnpTests.perHour',
    plm: '%',
    snoring: 'clpAndCnpTests.snoringUnit',
    tib: 'clpAndCnpTests.minutes',
    tst: 'clpAndCnpTests.minutes',
    se: '%',
    waso: 'clpAndCnpTests.minutes',
    sl: 'clpAndCnpTests.minutes',
    numberOfSleepCycles: 'clpAndCnpTests.perNight',
    n1: '%',
    n2: '%',
    n3: '%',
    rem: '%',
    wakeUpIndex: 'clpAndCnpTests.perHour',
    saO2LessThan88Percents: '%',
    saO2LessThan88Minutes: 'clpAndCnpTests.minutes',
    spO2LessThan90Percents: '%',
    vcLiters: fm('clpAndCnpTests.liters'),
    vcZValue: fm('clpAndCnpTests.zValue'),
    fvcLiters: fm('clpAndCnpTests.liters'),
    fvcZValue: fm('clpAndCnpTests.zValue'),
    fev1Liters: fm('clpAndCnpTests.liters'),
    fev1ZValue: fm('clpAndCnpTests.zValue'),
    fevPercents: '%',
    fevPercentsZValue: fm('clpAndCnpTests.zValue'),
    cheyneStokesRespirationMinutes: 'clpAndCnpTests.minutes',
    cheyneStokesRespirationPercents: '%',
  };
  return fieldName in units && units[fieldName]?.includes('.')
    ? fm(units[fieldName])
    : fieldName in units
      ? units[fieldName]
      : undefined;
};

const createClpAndCnpTestsTooltipDescription = (
  d: IDocumentWithTypeV2<
    'sleepPolygraphy' | 'sleepStudy' | 'spirometry' | 'nightOximetry' | 'carbonDioxideMeasurement'
  >,
  fields: string[],
  locPath: string,
  fm: (id: string) => string,
): IEvent['description'] => {
  return fields.map(field => {
    const f = field as keyof typeof d;
    return {
      title: field === 'madTherapyInUse' ? fm(locPath + 'madTherapyInUse') : fm(locPath + f),
      values:
        typeof d[f] === 'string'
          ? f in d
            ? fm(`${locPath}opts.sleepPolygraphy.${d[f]}`)
            : undefined
          : `${d[f] ?? '-'} ${getClpAndCnpTestsUnit(f, fm) ?? ''}`,
    };
  });
};

const isMadUseWanted = (mads: IDocumentWithTypeV2<'madTherapy'>[], date: PartialDate): boolean => {
  const dateValue = partialDateToValue(date);
  const boolArr = mads.map(md => {
    const startDateValue = md.date ? partialDateToValue(md.date) : 0;
    const endDateValue = md.endDate ? partialDateToValue(md.endDate) : Number.MAX_SAFE_INTEGER;
    if (startDateValue <= dateValue && dateValue <= endDateValue) {
      return true;
    } else {
      return false;
    }
  });
  if (boolArr.find(ele => ele === true)) return true;
  else return false;
};

export const convertClpAndCnpTestsToTimeline = (
  docs: Array<
    IDocumentWithTypeV2<'sleepPolygraphy' | 'sleepStudy' | 'spirometry' | 'nightOximetry' | 'carbonDioxideMeasurement'>
  >,
  madDocs: Array<IDocumentWithTypeV2<'madTherapy'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: IAddon[] = [];
  docs.forEach(d => {
    switch (d._type) {
      case 'sleepPolygraphy': {
        if (!isPartialDate(d.date)) return;
        const events: IEvent[] = [];
        const extraField = isMadUseWanted(madDocs, d.date) ? ['madTherapyInUse'] : [];
        const defaultFields = [
          'ahi',
          'ahiSupine',
          'rei',
          'reiSupine',
          'odi3',
          'cai',
          'averageSpO2',
          'spO2Below90',
          'lowestSpO2',
          'respiratoryRate',
          'tcCo2Max',
          'tcCo2Min',
          'incidenceOfLongTermStenosis',
          'flowRestrictionPercentage',
          'plmi',
          'plm',
        ];
        const wantedFields = extraField.concat(defaultFields);
        // if flowRestriction is found, insert that field field before plmi field
        d.flowRestriction ? wantedFields.splice(wantedFields.length - 1, 0, 'flowRestriction') : undefined;

        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'study',
          title: fm('clpAndCnpTests.sleepPolygraphy'),
          description: createClpAndCnpTestsTooltipDescription(d, wantedFields, 'clpAndCnpTests.', fm),
        });
        addons.push({
          id: d._type,
          title: fm('clpAndCnpTests.sleepPolygraphy'),
          titleDescription: undefined,
          items: [],
          events,
        });
        break;
      }
      case 'sleepStudy': {
        if (!isPartialDate(d.date)) return;
        const events: IEvent[] = [];
        const extraField = isMadUseWanted(madDocs, d.date) ? ['madTherapyInUse'] : [];
        const defaultFields = [
          'ahi',
          'ahiSupine',
          'odi3',
          'cai',
          'averageSpO2',
          'spO2Below90',
          'lowestSpO2',
          'respiratoryRate',
          'snoring',
          'incidenceOfLongTermStenosis',
          'tib',
          'tst',
          'se',
          'waso',
          'sl',
          'numberOfSleepCycles',
          'soremp',
          'n1',
          'n2',
          'n3',
          'rem',
          'wakeUpIndex',
          'plmi',
          'plm',
        ];
        const wantedFields = extraField.concat(defaultFields);
        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'study',
          title: fm('clpAndCnpTests.sleepStudy'),
          description: createClpAndCnpTestsTooltipDescription(d, wantedFields, 'clpAndCnpTests.', fm),
        });
        addons.push({
          id: d._type,
          title: fm('clpAndCnpTests.sleepStudy'),
          titleDescription: undefined,
          items: [],
          events,
        });
        break;
      }
      case 'spirometry': {
        if (!isPartialDate(d.date)) return;
        const events: IEvent[] = [];
        const dataFields: Array<keyof typeof d> = [
          'vcLiters',
          'vcZValue',
          'fvcLiters',
          'fvcZValue',
          'fev1Liters',
          'fev1ZValue',
          'fevPercents',
          'fevPercentsZValue',
        ];
        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'study',
          title: fm('clpAndCnpTests.spirometry'),
          description: createClpAndCnpTestsTooltipDescription(d, dataFields, 'clpAndCnpTests.graphLocalizations.', fm),
        });
        addons.push({
          id: d._type,
          title: fm('clpAndCnpTests.spirometry'),
          titleDescription: undefined,
          items: [],
          events,
        });
        break;
      }
      case 'nightOximetry': {
        if (!isPartialDate(d.date)) return;
        const events: IEvent[] = [];

        const dataFields: Array<keyof typeof d> = [
          'saO295thPercentile',
          'saO2Median',
          'saO2Minimum',
          'saO2LessThan88Percents',
          'saO2LessThan88Minutes',
          'spO2LessThan90Percents',
          'odi4',
          'pulse',
          'cheyneStokesRespirationMinutes',
          'cheyneStokesRespirationPercents',
        ];

        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'study',
          title: fm('clpAndCnpTests.nightOximetry'),
          description: createClpAndCnpTestsTooltipDescription(d, dataFields, 'clpAndCnpTests.', fm),
        });
        addons.push({
          id: d._type,
          title: fm('clpAndCnpTests.nightOximetry'),
          titleDescription: undefined,
          items: [],
          events,
        });
        break;
      }
      case 'carbonDioxideMeasurement': {
        if (!isPartialDate(d.date)) return;
        const events: IEvent[] = [];
        events.push({
          date: dateFromPartialUpdateTimeframe(d.date),
          eventType: 'study',
          title: fm('clpAndCnpTests.carbonDioxideMeasurement'),
          description: createClpAndCnpTestsTooltipDescription(
            d,
            ['tcCO2max', 'tcCO2mean', 'tcCO2min'],
            'clpAndCnpTests.',
            fm,
          ),
        });
        addons.push({
          id: d._type,
          title: fm('clpAndCnpTests.carbonDioxideMeasurement'),
          titleDescription: undefined,
          items: [],
          events,
        });
        break;
      }
    }
  });
  return addons.length > 0 ? addons : undefined;
};

// Helper function to aggregate PAP data into time periods
export const aggregateResmedDataByPeriod = (
  dataPoints: IDataPoint[],
  periodType: 'weekly' | 'monthly' | '6months' | '12months',
  fm: (id: string) => string,
  unit?: string,
  /** Count value average by days in the period instead of how many values were recorded */
  countAverageByDays = false,
): IDataPoint[] => {
  if (dataPoints.length === 0) return [];
  const sortedPoints = dataPoints.toSorted((a, b) => a.date.getTime() - b.date.getTime());
  const aggregatedPoints: IDataPoint[] = [];

  const getTimeWindow = (date: Date): number => {
    const inputDate = DateTime.fromJSDate(date).startOf('day');
    const currentDate = DateTime.now().startOf('day');

    switch (periodType) {
      case 'weekly': {
        // Calculate 7-day periods ending on current date
        const daysSinceInput = Math.floor(currentDate.diff(inputDate, 'days').days);
        const periodNumber = Math.floor(daysSinceInput / 7);
        const periodEndDate = currentDate.minus({ days: periodNumber * 7 });
        return periodEndDate.toMillis();
      }
      case 'monthly': {
        // Calculate 1-month periods ending on current date
        const monthsSinceInput = Math.floor(currentDate.diff(inputDate, 'months').months);
        const periodEndDate = currentDate.minus({ months: monthsSinceInput });
        return periodEndDate.toMillis();
      }
      case '6months': {
        // Calculate 6-month periods ending on current date
        const monthsSinceInput = Math.floor(currentDate.diff(inputDate, 'months').months);
        const periodNumber = Math.floor(monthsSinceInput / 6);
        const periodEndDate = currentDate.minus({ months: periodNumber * 6 });
        return periodEndDate.toMillis();
      }
      case '12months': {
        // Calculate 1-year periods ending on current date
        const yearsSinceInput = Math.floor(currentDate.diff(inputDate, 'years').years);
        const periodEndDate = currentDate.minus({ years: yearsSinceInput });
        return periodEndDate.toMillis();
      }
      default:
        return inputDate.toMillis();
    }
  };

  // Group data points by time windows
  const groupedData = new Map<number, IDataPoint[]>();
  sortedPoints.forEach(point => {
    const windowTime = getTimeWindow(point.date);
    if (!groupedData.has(windowTime)) {
      groupedData.set(windowTime, []);
    }
    groupedData.get(windowTime)!.push(point);
  });

  // Create aggregated points for each group
  groupedData.forEach((points, windowTime) => {
    const values = points.map(p => p.value).filter(v => typeof v === 'number');
    if (values.length === 0) return;

    // Calculate start date and period days based on period type using Luxon
    const endDateTime = DateTime.fromMillis(windowTime);
    const startDateTime =
      periodType === 'weekly'
        ? endDateTime.minus({ days: 6 })
        : periodType === 'monthly'
          ? endDateTime.minus({ months: 1 }).plus({ days: 1 })
          : periodType === '6months'
            ? endDateTime.minus({ months: 6 }).plus({ days: 1 })
            : endDateTime.minus({ years: 1 }).plus({ days: 1 });

    const actualPeriodDays = Math.ceil(endDateTime.diff(startDateTime, 'days').days) + 1;

    const sortedValues = [...values].sort((a, b) => a - b);
    const min = sortedValues[0];
    const max = sortedValues[sortedValues.length - 1];
    const avg = values.reduce((sum, val) => sum + val, 0) / (countAverageByDays ? actualPeriodDays : values.length);

    const startDate = startDateTime.toJSDate();

    aggregatedPoints.push({
      date: startDate,
      endDate: new Date(windowTime + 86400000 - 1), // add almost 24 hours to draw the bar at the end of the day
      value: avg,
      id: points[0]?.id ?? '',
      title: `${avg.toFixed(1)} ${unit}`,
      alternativeDateString: `${formatPartialDate(partialDateFromDate(startDate))} - ${formatPartialDate(partialDateFromDate(new Date(windowTime)))}`, //getResMedDateString(windowTime, periodType),
      description: [
        { title: fm('graph.resMed.min'), values: `${min?.toFixed(1)} ${unit}` },
        { title: fm('graph.resMed.max'), values: `${max?.toFixed(1)} ${unit}` },
        { title: fm('graph.resMed.avg'), values: `${avg.toFixed(1)} ${unit}` },
        { title: fm('graph.resMed.papUsageDays'), values: `${values.length}` },
      ],
    });
  });

  return aggregatedPoints;
};

export const convertPapToGraph = (
  docs: IPAPTherapy[],
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (docs.length === 0 && resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const primaryDataPoints: IDataPoint[] = [];
  const resMedDataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    (d.treatmentResponses ?? []).forEach(tr => {
      const value = Math.round(((tr.patientHoursOfUsePerDay ?? 0) + Number.EPSILON) * 100) / 100;
      exists(tr.patientHoursOfUsePerDay) &&
        tr.startDateOfPeriodUnderReview &&
        tr.date &&
        primaryDataPoints.push({
          date: dateFromPartialUpdateTimeframe(tr.date),
          value,
          id: 'pap',
          endDate: dateFromPartialUpdateTimeframe(tr.startDateOfPeriodUnderReview),
          title: `${value}`,
        });
    });
  });

  resMedTreatmentResponses.forEach(rtr => {
    if (!isPartialDate(rtr.date) || !isNumber(rtr.patientHoursOfUsePerDay)) return;
    resMedDataPoints.push({
      date: dateFromPartialUpdateTimeframe(rtr.date),
      value: rtr.patientHoursOfUsePerDay,
      id: 'resMedPap',
      title: `${rtr.patientHoursOfUsePerDay}`,
    });
  });

  const allDataPoints = [...primaryDataPoints, ...resMedDataPoints];
  if (allDataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  const dailyData: IData[] = [];
  if (primaryDataPoints.length > 0) {
    dailyData.push({
      dataPoints: primaryDataPoints,
      id: 'pap',
      type: 'constantValueChart',
      legend: fm('treatment.papTherapy.patientHoursOfUse'),
    });
  }
  if (resMedDataPoints.length > 0) {
    dailyData.push({
      dataPoints: resMedDataPoints,
      id: 'resMedPap',
      type: 'lineGraph',
      legend: `${fm('treatment.papTherapy.patientHoursOfUse')} (ResMed)`,
      linegraphProps: {
        hidePoints: true,
        doNotMergeOverlappingDataPoints: true,
      },
    });
  }
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    if (resMedDataPoints.length > 0) {
      const aggregatedResMed = aggregateResmedDataByPeriod(resMedDataPoints, key, fm, 'h', true);
      if (aggregatedResMed.length > 0) {
        periodData.push({
          dataPoints: aggregatedResMed,
          id: 'resMedPap',
          type: 'stackedBarChart',
          legend: `${fm('treatment.papTherapy.patientHoursOfUse')} (ResMed)`,
          stackedBarChartProps: { interval: barInterval },
        });
      }
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertAHIToGraph = (
  papTherapyDocs: Array<IDocumentWithTypeV2<'papTherapy'>>,
  sleepPolygraphyDocs: Array<IDocumentWithTypeV2<'sleepPolygraphy'>>,
  sleepStudyDocs: Array<IDocumentWithTypeV2<'sleepStudy'>>,
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (
    papTherapyDocs.length === 0 &&
    sleepPolygraphyDocs.length === 0 &&
    sleepStudyDocs.length === 0 &&
    resMedTreatmentResponses.length === 0
  )
    return undefined;

  // Collect all data points (same as original logic)
  // ResMed AHI datapoints
  const resMedAhiDataPoints: IDataPoint[] = [];
  // AHI datapoints
  const ahiDataPoints: IDataPoint[] = [];
  // REI datapoints
  const reiDataPoints: IDataPoint[] = [];
  // Secondarydata for residue ahi
  const secondaryDataPoints: IDataPoint[] = [];

  // Handle ResMed docs
  resMedTreatmentResponses.forEach(rtr => {
    if (!isPartialDate(rtr.date) || !isNumber(rtr.ahi)) return;
    resMedAhiDataPoints.push({
      date: dateFromPartialUpdateTimeframe(rtr.date),
      value: rtr.ahi,
      id: 'resMedAhi',
      title: `${rtr.ahi} / h`,
    });
  });

  // Handle pap therapy docs here
  papTherapyDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    (d.treatmentResponses ?? []).forEach(tr => {
      isNumber(tr.ahi) &&
        tr.date &&
        secondaryDataPoints.push({
          date: dateFromPartialUpdateTimeframe(tr.date),
          value: tr.ahi,
          id: 'residueAhi',
          endDate: tr.startDateOfPeriodUnderReview
            ? dateFromPartialUpdateTimeframe(tr.startDateOfPeriodUnderReview)
            : undefined,
          title: `${tr.ahi}`,
        });
    });
  });
  // Handle sleep polygraphy docs here
  sleepPolygraphyDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const valueAhi = d.ahi || d.ahi === 0 ? d.ahi : undefined;
    const valueRei = d.rei || d.rei === 0 ? d.rei : undefined;
    valueAhi &&
      ahiDataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: valueAhi,
        id: 'ahi',
        endDate: dateFromPartialUpdateTimeframe(d.date),
        title: `${valueAhi}`,
        description: [{ title: fm('clpAndCnpTests.sleepPolygraphy') }],
      });
    valueRei &&
      reiDataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: valueRei,
        id: 'rei',
        endDate: dateFromPartialUpdateTimeframe(d.date),
        title: `${valueRei}`,
        description: [{ title: fm('clpAndCnpTests.sleepPolygraphy') }],
      });
  });
  // Handle sleep study docs here
  sleepStudyDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = d.ahi || d.ahi === 0 ? d.ahi : undefined;
    value &&
      ahiDataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value,
        id: 'ahi',
        endDate: dateFromPartialUpdateTimeframe(d.date),
        title: `${value}`,
        description: [{ title: fm('clpAndCnpTests.sleepStudy') }],
      });
  });

  const allDataPoints = [...resMedAhiDataPoints, ...ahiDataPoints, ...reiDataPoints, ...secondaryDataPoints];
  if (allDataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  if (secondaryDataPoints.length > 0) {
    dailyData.push({
      dataPoints: secondaryDataPoints,
      id: 'residueAhi',
      type: 'constantValueChart',
      legend: fm('graph.residueAhi'),
    });
  }
  if (ahiDataPoints.length > 0) {
    dailyData.push({
      dataPoints: ahiDataPoints,
      id: 'ahi',
      type: 'lineGraph',
      legend: fm('clpAndCnpTests.ahi'),
    });
  }
  if (reiDataPoints.length > 0) {
    dailyData.push({
      dataPoints: reiDataPoints,
      id: 'rei',
      type: 'lineGraph',
      legend: fm('clpAndCnpTests.rei'),
    });
  }
  if (resMedAhiDataPoints.length > 0) {
    dailyData.push({
      dataPoints: resMedAhiDataPoints,
      id: 'resMedAhi',
      type: 'lineGraph',
      legend: `${fm('clpAndCnpTests.ahi')} (ResMed)`,
      linegraphProps: {
        hidePoints: true,
        doNotMergeOverlappingDataPoints: true,
      },
    });
  }
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    if (ahiDataPoints.length > 0) {
      periodData.push({
        dataPoints: ahiDataPoints,
        id: 'ahi',
        type: 'lineGraph',
        legend: fm('clpAndCnpTests.ahi'),
      });
    }

    if (reiDataPoints.length > 0) {
      periodData.push({
        dataPoints: reiDataPoints,
        id: 'rei',
        type: 'lineGraph',
        legend: fm('clpAndCnpTests.rei'),
      });
    }

    if (resMedAhiDataPoints.length > 0) {
      const aggregatedResMed = aggregateResmedDataByPeriod(resMedAhiDataPoints, key, fm, '/ h');
      if (aggregatedResMed.length > 0) {
        periodData.push({
          dataPoints: aggregatedResMed,
          id: 'resMedAhi',
          type: 'stackedBarChart',
          legend: `${fm('clpAndCnpTests.ahi')} (ResMed)`,
          stackedBarChartProps: { interval: barInterval },
        });
      }
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convert95thPercentilePressureToGraph = (
  docs: Array<IDocumentWithTypeV2<'papTherapy'>>,
  allDocs: Array<IDocumentV2>,
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  const hasSleepApnea = patientHasSleepApneaDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  // If patient didnt have self adjusting cpap device, do not draw anything
  const hasSelfAdjustingCPAPDevice = !!docs.find(d => {
    if ((d.devices ?? []).find(dev => dev.type === 'selfAdjustingCPAP' || dev.type === 'autosetForHer')) return true;
    return false;
  });
  if (
    (docs.length === 0 && resMedTreatmentResponses.length === 0) ||
    !hasSleepApnea ||
    (!hasSelfAdjustingCPAPDevice && resMedTreatmentResponses.length === 0)
  ) {
    return undefined;
  }

  // Collect all data points (same as original logic)
  const primaryDataPoints: IDataPoint[] = [];
  const values: { startDate: PartialDate; endDate: PartialDate; value: number }[] = [];
  const resMedDataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    // Push pressure-values into a variable called 'values'
    (d.treatmentResponses ?? []).forEach(tr => {
      tr.startDateOfPeriodUnderReview &&
        tr.date &&
        isPartialDate(tr.startDateOfPeriodUnderReview) &&
        isPartialDate(tr.date) &&
        exists(tr.pressure95Percentile) &&
        values.push({
          startDate: tr.startDateOfPeriodUnderReview,
          endDate: tr.date,
          value: tr.pressure95Percentile ?? 0,
        });
    });
  });
  // Iterate through the values array and make a datapoint out of each element of the values-array
  values.forEach(v => {
    primaryDataPoints.push({
      date: dateFromPartialUpdateTimeframe(v.endDate),
      value: v.value,
      id: 'pressure95thPercentile',
      endDate: dateFromPartialUpdateTimeframe(v.startDate),
      title: `${v.value} cmH2O`,
    });
  });

  resMedTreatmentResponses.forEach(tr => {
    if (tr.pressure95Percentile && isPartialDate(tr.date)) {
      resMedDataPoints.push({
        date: dateFromPartialUpdateTimeframe(tr.date),
        value: tr.pressure95Percentile,
        id: 'resMedPressure95Percentile',
        title: `${tr.pressure95Percentile} cmH2O`,
      });
    }
  });

  const allDataPoints = [...primaryDataPoints, ...resMedDataPoints];
  if (allDataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  if (primaryDataPoints.length > 0) {
    dailyData.push({
      dataPoints: primaryDataPoints,
      id: 'pressure95thPercentile',
      type: 'constantValueChart',
      legend: fm('treatment.papTherapy.pressure95Percentile'),
    });
  }
  if (resMedDataPoints.length > 0) {
    dailyData.push({
      dataPoints: resMedDataPoints,
      id: 'resMedPressure95Percentile',
      type: 'lineGraph',
      legend: `${fm('treatment.papTherapy.pressure95Percentile')} (ResMed)`,
      linegraphProps: {
        hidePoints: true,
        doNotMergeOverlappingDataPoints: true,
      },
    });
  }
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    if (resMedDataPoints.length > 0) {
      const aggregatedResMed = aggregateResmedDataByPeriod(resMedDataPoints, key, fm, 'cmH2O');
      if (aggregatedResMed.length > 0) {
        periodData.push({
          dataPoints: aggregatedResMed,
          id: 'resMedPressure95Percentile',
          type: 'stackedBarChart',
          legend: `${fm('treatment.papTherapy.pressure95Percentile')} (ResMed)`,
          stackedBarChartProps: { interval: barInterval },
        });
      }
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convert95thPercentileMaskLeakToGraph = (
  docs: Array<IDocumentWithTypeV2<'papTherapy'>>,
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (docs.length === 0 && resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const primaryDataPoints: IDataPoint[] = [];
  const resMedDataPoints: IDataPoint[] = [];

  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    (d.treatmentResponses ?? []).forEach(tr => {
      isNumber(tr.maskLeakage95PercentilePerMinute) &&
        tr.startDateOfPeriodUnderReview &&
        tr.date &&
        primaryDataPoints.push({
          date: dateFromPartialUpdateTimeframe(tr.date),
          value: tr.maskLeakage95PercentilePerMinute,
          id: 'maskLeak95thPercentile',
          endDate: dateFromPartialUpdateTimeframe(tr.startDateOfPeriodUnderReview),
          title: `${tr.maskLeakage95PercentilePerMinute} l/min`,
        });
    });
  });

  resMedTreatmentResponses.forEach(rtr => {
    if (!isPartialDate(rtr.date) || !isNumber(rtr.maskLeakage95PercentilePerMinute)) return;
    resMedDataPoints.push({
      date: dateFromPartialUpdateTimeframe(rtr.date),
      value: rtr.maskLeakage95PercentilePerMinute,
      id: 'resMedMaskLeak95Percentile',
      title: `${rtr.maskLeakage95PercentilePerMinute} l/min`,
    });
  });

  const allDataPoints = [...primaryDataPoints, ...resMedDataPoints];
  if (allDataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  if (primaryDataPoints.length > 0) {
    dailyData.push({
      dataPoints: primaryDataPoints,
      id: 'maskLeak95thPercentile',
      type: 'constantValueChart',
      legend: fm('treatment.papTherapy.maskLeakage95PercentilePerMinute'),
    });
  }
  if (resMedDataPoints.length > 0) {
    dailyData.push({
      dataPoints: resMedDataPoints,
      id: 'resMedMaskLeak95Percentile',
      type: 'lineGraph',
      legend: `${fm('treatment.papTherapy.maskLeakage95PercentilePerMinute')} (ResMed)`,
      linegraphProps: {
        hidePoints: true,
        doNotMergeOverlappingDataPoints: true,
      },
    });
  }
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    if (resMedDataPoints.length > 0) {
      const aggregatedResMed = aggregateResmedDataByPeriod(resMedDataPoints, key, fm, 'l/min');
      if (aggregatedResMed.length > 0) {
        periodData.push({
          dataPoints: aggregatedResMed,
          id: 'resMedMaskLeak95Percentile',
          type: 'stackedBarChart',
          legend: `${fm('treatment.papTherapy.maskLeakage95PercentilePerMinute')} (ResMed)`,
          stackedBarChartProps: { interval: barInterval },
        });
      }
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertTcCO2MeanToGraph = (
  docs: Array<IDocumentWithTypeV2<'carbonDioxideMeasurement'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  const hasRespiratoryFailure = patientHasRespiratoryFailureDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  if (docs.length === 0 || !hasRespiratoryFailure) return undefined;
  const primaryData: IData[] = [];
  const primaryDataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date) || !exists(d.tcCO2mean)) return;
    exists(d.tcCO2mean) &&
      primaryDataPoints.push({
        date: dateFromPartialUpdateTimeframe(d.date),
        value: d.tcCO2mean ?? 0,
        id: 'tcCO2Mean',
        title: `${d.tcCO2mean ?? '-'} ${fm('clpAndCnpTests.kPa')}`,
      });
  });
  primaryDataPoints.length > 0 &&
    primaryData.push({
      dataPoints: primaryDataPoints,
      id: 'tcCO2Mean',
      type: 'lineGraph',
      legend: fm('clpAndCnpTests.tcCO2mean'),
    });
  return primaryData.length > 0 ? primaryData : undefined;
};

export const convertOxygenFlowToGraph = (
  oxygenTherapyDocs: Array<IDocumentWithTypeV2<'oxygenTherapy'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  const hasRespiratoryFailure = patientHasRespiratoryFailureDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  if (oxygenTherapyDocs.length === 0 || !hasRespiratoryFailure) return undefined;
  const data: IData[] = [];

  ['oxygenConcentratorTherapeutic', 'oxygenConcentratorPalliative', 'stressOxygen'].forEach((t, i) => {
    const primaryDataPoints: IDataPoint[] = [];
    const secondaryDataPoints: IDataPoint[] = [];
    const primaryDataPointsPercentage: IDataPoint[] = [];
    const secondaryDataPointsPercentage: IDataPoint[] = [];

    oxygenTherapyDocs.forEach(d => {
      if (!d.oxygenTherapyType || d.oxygenTherapyType !== t) return;
      if (!isPartialDate(d.date)) return;
      const settings = d.settings ? [...d.settings] : [];
      settings
        .sort((s1, s2) => sortPartialDate(s1.date, s2.date))
        .forEach((s, i) => {
          const endDate: PartialDate | undefined = settings[i + 1]?.date ?? d.endDate ?? undefined;

          if (s.date && (exists(s.flowMin) || exists(s.flowMax))) {
            const common = {
              date: dateFromPartialUpdateTimeframe(s.date),
              endDate: endDate ? dateFromPartialUpdateTimeframe(endDate) : undefined,
            };

            switch (s.implementationOfTreatment) {
              case 'venturiMask': {
                if (exists(s.flowMin)) {
                  primaryDataPointsPercentage.push({
                    ...common,
                    value: s.flowMin!,
                    id: 'oxygenFlowPercent',
                    title: `${s.flowMin} %`,
                  });
                }
                if (exists(s.flowMax)) {
                  secondaryDataPointsPercentage.push({
                    ...common,
                    value: s.flowMax!,
                    id: 'oxygenFlowPercentMax',
                    title: `${s.flowMax} %`,
                  });
                }
                break;
              }
              default: {
                if (exists(s.flowMin)) {
                  primaryDataPoints.push({
                    ...common,
                    value: s.flowMin!,
                    id: 'oxygenFlow',
                    title: `${s.flowMin} l/min`,
                  });
                }
                if (exists(s.flowMax)) {
                  secondaryDataPoints.push({
                    ...common,
                    value: s.flowMax!,
                    id: 'oxygenFlowMax',
                    title: `${s.flowMax} l/min`,
                  });
                }
                break;
              }
            }
          }
        });
    });

    primaryDataPoints.length > 0 &&
      data.push({
        dataPoints: primaryDataPoints,
        id: `oxygenFlow${i}`,
        type: 'constantValueChart',
        legend:
          fm('graph.oxygenFlow') +
          ` ${fm('general.minimum')}`.toLocaleLowerCase() +
          ` (${fm(`treatment.oxygenTherapy.opts.${t}`)})`,
      });
    secondaryDataPoints.length > 0 &&
      data.push({
        dataPoints: secondaryDataPoints,
        id: `oxygenFlowMax${i}`,
        type: 'constantValueChart',
        legend:
          fm('graph.oxygenFlow') +
          ` ${fm('general.maximum')}`.toLocaleLowerCase() +
          ` (${fm(`treatment.oxygenTherapy.opts.${t}`)})`,
      });

    primaryDataPointsPercentage.length > 0 &&
      data.push({
        dataPoints: primaryDataPointsPercentage,
        id: `oxygenFlowPercent${i}`,
        type: 'constantValueChart',
        legend:
          fm('graph.oxygenFlow') +
          ` ${fm('general.minimum')}`.toLocaleLowerCase() +
          ` (${fm(`treatment.oxygenTherapy.opts.${t}`)})`,
      });
    secondaryDataPointsPercentage.length > 0 &&
      data.push({
        dataPoints: secondaryDataPointsPercentage,
        id: `oxygenFlowPercentMax${i}`,
        type: 'constantValueChart',
        legend:
          fm('graph.oxygenFlow') +
          ` ${fm('general.maximum')}`.toLocaleLowerCase() +
          ` (${fm(`treatment.oxygenTherapy.opts.${t}`)})`,
      });
  });
  return data.length > 0 ? data : undefined;
};

export const convertOxygenSaturationToGraph = (
  docs: Array<IDocumentWithTypeV2<'oxygenTherapy'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  const hasRespiratoryFailure = patientHasRespiratoryFailureDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  if (docs.length === 0 || !hasRespiratoryFailure) return undefined;
  const data: IData[] = [];

  ['oxygenConcentratorTherapeutic', 'oxygenConcentratorPalliative', 'stressOxygen'].forEach((t, i) => {
    const dataPoints: IDataPoint[] = [];

    docs.forEach(d => {
      if (!d.oxygenTherapyType || d.oxygenTherapyType !== t) return;
      if (!isPartialDate(d.date)) return;
      (d.treatmentResponses ?? [])
        .toSorted((s1, s2) => sortPartialDate(s1.date, s2.date))
        .forEach((tr, i) => {
          const endDate: PartialDate | undefined = d.treatmentResponses?.[i + 1]?.date ?? d.endDate ?? undefined;
          isNumber(tr.oxygenSaturation) &&
            tr.date &&
            dataPoints.push({
              date: dateFromPartialUpdateTimeframe(tr.date),
              value: tr.oxygenSaturation,
              endDate: endDate ? dateFromPartialUpdateTimeframe(endDate) : undefined,
              id: 'oxygenSaturation',
              title: `${tr.oxygenSaturation} %`,
            });
        });
    });

    data.push({
      dataPoints,
      id: `oxygenSaturation${i}`,
      type: 'constantValueChart',
      legend: fm('graph.oxygenSaturation') + ` (${fm(`treatment.oxygenTherapy.opts.${t}`)})`,
    });
  });
  return data;
};

const sortTime = (time1?: Time, time2?: Time): number => {
  if (!time1 && !time2) return 0;
  if (!time1) return -1;
  if (!time2) return 1;
  if (time1[0] > time2[0]) return 1;
  if (time2[0] > time1[0]) return -1;
  if (time1[0] === time2[0] && time1[1] > time2[1]) return 1;
  if (time1[0] === time2[0] && time2[1] > time1[1]) return -1;
  return 0;
};

export const convertFlowRateToGraph = (
  hfncTherapyDocs: Array<IDocumentWithTypeV2<'hfncTherapy'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  const hasRespiratoryFailure = patientHasRespiratoryFailureDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  if (hfncTherapyDocs.length === 0 || !hasRespiratoryFailure) return undefined;
  const data: IData[] = [];
  const primaryDataPoints: IDataPoint[] = [];
  hfncTherapyDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    (d.settings ?? [])
      .toSorted((s1, s2) => sortPartialDate(s1.date, s2.date) || sortTime(s1.time, s2.time))
      .forEach((s, i) => {
        const endDate: PartialDate | undefined = d.settings?.[i + 1]?.date ?? d.endDate ?? undefined;
        const endTime: Time | undefined = d.settings?.[i + 1]?.time ?? [23, 59];
        exists(s.flowRate) &&
          s.date &&
          primaryDataPoints.push({
            date: dateFromPartialUpdateTimeframe(s.date, s.time),
            value: s.flowRate ?? 0,
            time: s.time,
            id: 'flowRate',
            endDate: endDate ? dateFromPartialUpdateTimeframe(endDate, endTime) : undefined,
            title: `${s.flowRate ?? '-'} l/min`,
            description: s.time ? formatTime(s.time) : undefined,
          });
      });
  });
  primaryDataPoints.length > 0 &&
    data.push({
      dataPoints: primaryDataPoints,
      id: 'flowRate',
      type: 'constantValueChart',
      legend: fm('graph.airFlowLegend'),
    });
  return data.length > 0 ? data : undefined;
};

export const convertSupplementalOxygenToGraph = (
  hfncTherapyDocs: Array<IDocumentWithTypeV2<'hfncTherapy'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  const hasRespiratoryFailure = patientHasRespiratoryFailureDiagnosis(filterDocumentsByType(allDocs, ['diagnosis']));
  if (hfncTherapyDocs.length === 0 || !hasRespiratoryFailure) return undefined;
  const data: IData[] = [];
  const primaryDataPoints: IDataPoint[] = [];
  hfncTherapyDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    (d.settings ?? [])
      .toSorted((s1, s2) => sortPartialDate(s1.date, s2.date))
      .forEach((s, i) => {
        const endDate: PartialDate | undefined = d.settings?.[i + 1]?.date ?? d.endDate ?? undefined;
        const endTime: Time | undefined = d.settings?.[i + 1]?.time ?? [23, 59];
        exists(s.supplementalOxygen) &&
          s.date &&
          primaryDataPoints.push({
            date: dateFromPartialUpdateTimeframe(s.date, s.time),
            value: s.supplementalOxygen ?? 0,
            time: s.time,
            id: 'supplementalOxygen',
            endDate: endDate ? dateFromPartialUpdateTimeframe(endDate, endTime) : undefined,
            title: `${s.supplementalOxygen ?? '-'} %`,
            description: s.time ? formatTime(s.time) : undefined,
          });
      });
  });
  primaryDataPoints.length > 0 &&
    data.push({
      dataPoints: primaryDataPoints,
      id: 'supplementalOxygen',
      type: 'constantValueChart',
      legend: fm('graph.oxygenPercentageLegend'),
    });
  return data.length > 0 ? data : undefined;
};

/**
 * Get a localizer for PAP therapy end reason.
 *
 * @param fm Intl localizer
 * @returns PAP therapy end reason localizer
 */
const getPapTherapyEndReasonLocalizer =
  (fm: (localizationKey: string) => string): FDataFieldLocalizer<IPAPTherapy> =>
  papTherapy => {
    // join the localized end reasons separated with a comma
    if (papTherapy.endReason) {
      return papTherapy.endReason.map(reason => fm(`treatment.opts.papTherapyEndReason.${reason}`)).join(', ');
    }

    // fallback if there is no end reason
    else return '-';
  };

export const convertAiCentralToGraph = (
  papTherapyDocs: Array<IDocumentWithTypeV2<'papTherapy'>>,
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  allDocs: Array<IDocumentV2>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  const papDataPoints: IDataPoint[] = [];

  resMedTreatmentResponses.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.aiCentral)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.aiCentral,
      id: 'aiCentral',
      title: `${d.aiCentral} %`,
    });
  });

  papTherapyDocs.forEach(ptd => {
    (ptd.treatmentResponses ?? []).forEach(tr => {
      patientHasSleepApneaDiagnosis(filterDocumentsByType(allDocs, ['diagnosis'])) &&
        isNumber(tr.aiCentral) &&
        tr.date &&
        papDataPoints.push({
          date: dateFromPartialUpdateTimeframe(tr.date),
          value: tr.aiCentral,
          id: 'residueCai',
          endDate: tr.startDateOfPeriodUnderReview
            ? dateFromPartialUpdateTimeframe(tr.startDateOfPeriodUnderReview)
            : undefined,
          title: `${tr.aiCentral}`,
        });
    });
  });

  const allDataPoints = [...dataPoints, ...papDataPoints];
  if (allDataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  if (dataPoints.length > 0) {
    dailyData.push({
      dataPoints,
      id: 'aiCentral',
      type: 'lineGraph',
      legend: fm('graph.aiCentral'),
      linegraphProps: {
        hidePoints: true,
        doNotMergeOverlappingDataPoints: true,
      },
    });
  }
  if (papDataPoints.length > 0) {
    dailyData.push({
      dataPoints: papDataPoints,
      id: 'residueCai',
      type: 'constantValueChart',
      legend: fm('graph.residueCai'),
    });
  }
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    if (dataPoints.length > 0) {
      const aggregatedData = aggregateResmedDataByPeriod(dataPoints, key, fm);
      if (aggregatedData.length > 0) {
        periodData.push({
          dataPoints: aggregatedData,
          id: 'aiCentral',
          type: 'stackedBarChart',
          legend: fm('graph.aiCentral'),
          stackedBarChartProps: { interval: barInterval },
        });
      }
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertTidalVolume95PercentileToGraph = (
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  resMedTreatmentResponses.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.tidalVolume95Percentile)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.tidalVolume95Percentile,
      id: 'tidalVolume95Percentile',
      title: `${d.tidalVolume95Percentile} ml`,
    });
  });

  if (dataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  dailyData.push({
    dataPoints,
    id: 'tidalVolume95Percentile',
    type: 'lineGraph',
    legend: fm('treatment.papTherapy.tidalVolume95Percentile'),
    linegraphProps: {
      hidePoints: true,
      doNotMergeOverlappingDataPoints: true,
    },
  });
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    const aggregatedData = aggregateResmedDataByPeriod(dataPoints, key, fm, 'ml');
    if (aggregatedData.length > 0) {
      periodData.push({
        dataPoints: aggregatedData,
        id: 'tidalVolume95Percentile',
        type: 'stackedBarChart',
        legend: fm('treatment.papTherapy.tidalVolume95Percentile'),
        stackedBarChartProps: { interval: barInterval },
      });
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertRespiratoryRate95PercentileToGraph = (
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  resMedTreatmentResponses.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.respiratoryRate95Percentile)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.respiratoryRate95Percentile,
      id: 'respiratoryRate95Percentile',
      title: `${d.respiratoryRate95Percentile} ${fm('treatment.papTherapy.inhalationsPerMinute')}`,
    });
  });

  if (dataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  dailyData.push({
    dataPoints,
    id: 'respiratoryRate95Percentile',
    type: 'lineGraph',
    legend: fm('treatment.papTherapy.respiratoryRate95Percentile'),
    linegraphProps: {
      hidePoints: true,
      doNotMergeOverlappingDataPoints: true,
    },
  });
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    const aggregatedData = aggregateResmedDataByPeriod(
      dataPoints,
      key,
      fm,
      fm('treatment.papTherapy.inhalationsPerMinute'),
    );
    if (aggregatedData.length > 0) {
      periodData.push({
        dataPoints: aggregatedData,
        id: 'respiratoryRate95Percentile',
        type: 'stackedBarChart',
        legend: fm('treatment.papTherapy.respiratoryRate95Percentile'),
        stackedBarChartProps: { interval: barInterval },
      });
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertSpontTToGraph = (
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  resMedTreatmentResponses.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.spontT)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.spontT,
      id: 'spontT',
      title: `${d.spontT} %`,
    });
  });

  if (dataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  dailyData.push({
    dataPoints,
    id: 'spontT',
    type: 'lineGraph',
    legend: 'Spont T',
    linegraphProps: {
      hidePoints: true,
      doNotMergeOverlappingDataPoints: true,
    },
  });
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    const aggregatedData = aggregateResmedDataByPeriod(dataPoints, key, fm, '%');
    if (aggregatedData.length > 0) {
      periodData.push({
        dataPoints: aggregatedData,
        id: 'spontT',
        type: 'stackedBarChart',
        legend: 'Spont T',
        stackedBarChartProps: { interval: barInterval },
      });
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertSpontCToGraph = (
  resMedTreatmentResponses: Array<IReadonlyDocumentWithType<'resMedTreatmentResponse'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedTreatmentResponses.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  resMedTreatmentResponses.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.spontC)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.spontC,
      id: 'spontC',
      title: `${d.spontC} %`,
    });
  });

  if (dataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  dailyData.push({
    dataPoints,
    id: 'spontC',
    type: 'lineGraph',
    legend: 'Spont C',
    linegraphProps: {
      hidePoints: true,
      doNotMergeOverlappingDataPoints: true,
    },
  });
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    const aggregatedData = aggregateResmedDataByPeriod(dataPoints, key, fm, '%');
    if (aggregatedData.length > 0) {
      periodData.push({
        dataPoints: aggregatedData,
        id: 'spontC',
        type: 'stackedBarChart',
        legend: 'Spont C',
        stackedBarChartProps: { interval: barInterval },
      });
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};

export const convertTargetVentilationToGraph = (
  resMedSettings: Array<IReadonlyDocumentWithType<'resMedSetting'>>,
  dateFromPartialUpdateTimeframe: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<{ data: IData[]; id: string }> | undefined => {
  if (resMedSettings.length === 0) return undefined;

  // Collect all data points (same as original logic)
  const dataPoints: IDataPoint[] = [];
  resMedSettings.forEach(d => {
    if (!isPartialDate(d.date) || !isNumber(d.targetVa)) return;
    dataPoints.push({
      date: dateFromPartialUpdateTimeframe(d.date),
      value: d.targetVa,
      id: 'targetVentilation',
      title: `${d.targetVa} l/min`,
    });
  });

  if (dataPoints.length === 0) return undefined;

  const tabs: Array<{ data: IData[]; id: string }> = [];

  // Create daily tab
  const dailyData: IData[] = [];
  dailyData.push({
    dataPoints,
    id: 'targetVentilation',
    type: 'lineGraph',
    legend: fm('treatment.papTherapy.targetVentilation'),
    linegraphProps: {
      hidePoints: true,
      doNotMergeOverlappingDataPoints: true,
    },
  });
  tabs.push({ data: dailyData, id: fm('graph.resMed.tabs.daily') });

  const periods: Array<'weekly' | 'monthly' | '6months' | '12months'> = ['weekly', 'monthly', '6months', '12months'];

  periods.forEach(key => {
    const periodData: IData[] = [];
    const barInterval = key === '12months' ? 'yearly' : key === '6months' ? 'halfYearly' : key;

    const aggregatedData = aggregateResmedDataByPeriod(dataPoints, key, fm, 'l/min');
    if (aggregatedData.length > 0) {
      periodData.push({
        dataPoints: aggregatedData,
        id: 'targetVentilation',
        type: 'stackedBarChart',
        legend: fm('treatment.papTherapy.targetVentilation'),
        stackedBarChartProps: { interval: barInterval },
      });
    }

    if (periodData.length > 0) {
      tabs.push({ data: periodData, id: fm(`graph.resMed.tabs.${key}`) });
    }
  });

  return tabs.length > 0 ? tabs : undefined;
};
